import React, { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { CalendarController } from "../CalendarController/CalendarController";
import { CalendarLabel } from "../CalendarLabel/CalendarLabel";
import { Layout } from "../Layouts";
import { WeeklyCalendar, WeeklyCalendarProps } from "./WeeklyCalendar";

export const useWeeklyCalendar = ({
  date = undefined,
  startWeekday = 0,
  onChange,
}: {
  date?: dayjs.ConfigType;
  startWeekday?: number;
  onChange?: (date: Date) => void;
} = {}) => {
  const initialDate = useMemo(() => {
    if (date === null || date === undefined) {
      return dayjs().day(startWeekday);
    }
    return dayjs(date).day(startWeekday);
  }, [date, startWeekday]);
  const [sunday, setSunday] = useState(initialDate);
  const increment = useCallback(() => {
    setSunday((prev) => {
      const next = prev.add(7, "day");
      if (onChange) {
        onChange(next.toDate());
      }
      return next;
    });
  }, [onChange]);
  const decrement = useCallback(() => {
    setSunday((prev) => {
      const next = prev.subtract(7, "day");
      if (onChange) {
        onChange(next.toDate());
      }
      return next;
    });
  }, [onChange]);

  const reset = useCallback(() => {
    if (onChange) {
      onChange(initialDate.toDate());
    }
    setSunday(initialDate);
  }, [initialDate, onChange]);
  const set = useCallback(
    (config: dayjs.ConfigType) => {
      const next = dayjs(config).day(startWeekday);
      if (onChange) {
        onChange(next.toDate());
      }
      setSunday(next);
    },
    [onChange, startWeekday],
  );
  return {
    date: sunday,
    increment,
    decrement,
    reset,
    set,
  };
};

export interface WeeklyCalendarUIProps<T> extends Omit<
  WeeklyCalendarProps<T>,
  "date"
> {
  style?: React.CSSProperties;
  className?: string;
  startWeekday?: number;
  date?: dayjs.ConfigType;
  onChange?: (date: Date) => void;
}

export const WeeklyCalendarUI = <T,>({
  style,
  className,
  date: initialDate,
  startWeekday,
  onChange,
  ...props
}: WeeklyCalendarUIProps<T>) => {
  const { date, set, increment, decrement } = useWeeklyCalendar({
    date: initialDate,
    startWeekday,
    onChange,
  });

  return (
    <div style={style} className={className}>
      <Layout.Column gap={2} nowrap>
        <div>
          <Layout.Row nowrap alignItems="center" justify="space-between">
            <div>
              <CalendarController
                unit="day"
                onReset={set}
                onNext={increment}
                onPrev={decrement}
              />
            </div>
            <div>
              <CalendarLabel date={date} />
            </div>
          </Layout.Row>
        </div>
        <WeeklyCalendar date={date} {...props} />
      </Layout.Column>
    </div>
  );
};
